import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { SurveyService } from '../../../services/surveys.service';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject, timer, of, interval, Subscription, merge } from 'rxjs';
import { takeUntil, filter, concatMap, switchMap, debounceTime, catchError } from 'rxjs/operators';
import { ToastService } from '../../../services/general/toast.service';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { OrganizationService } from '../../../services/organization.service';
import { UserService } from '../../../services/user.service';
import { AlertService } from '../../../services/general/alert.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ModalShowSurveysComponent } from '../../../modals/modal-show-surveys/modal-show-surveys.component';
import { CkmService } from '../../../services/ckm.service';
import { ModalSurveyDetailsComponent } from '../../../modals/modal-survey-details/modal-survey-details.component';
import { ModalShareQrComponent } from '../../../modals/modal-share-qr/modal-share-qr.component';
import { ModalShowEvidencesComponent } from '../../../modals/modal-show-evidences/modal-show-evidences.component';
import { SingleQuestionComponent } from '../single-question/single-question.component';
import { HeaderTitleService } from '../../../services/general/header-title.service';
import {
  faLock,
  faGlobe,
  faCheckCircle,
  faInfoCircle,
  faClipboardList,
  faSlidersH,
  faUsers,
  faPaperclip,
} from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-survey-details',
  templateUrl: './survey-details.component.html',
  styleUrls: ['./survey-details.component.scss'],
  standalone: false,
})
export class SurveyDetailsComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChildren(SingleQuestionComponent)
  questionComponents!: QueryList<SingleQuestionComponent>;
  @Input() projectId: any;
  @Input() surveyId: any;
  @Input() surveyObj: any;
  @Input() isResponse = false;
  @Input() noButtons = false;
  @Input() ckmMapID = null;
  @Output() surveyAdded = new EventEmitter();
  @Output() canceled = new EventEmitter();
  @Output() surveyChanged = new EventEmitter();
  @ViewChild('form') form!: NgForm;

  constructor(
    public surveyService: SurveyService,
    public translateService: TranslateService,
    public toast: ToastService,
    public route: ActivatedRoute,
    public organizationService: OrganizationService,
    public userService: UserService,
    public alert: AlertService,
    public router: Router,
    public dialog: MatDialog,
    public ckmService: CkmService,
    private headerTitleService: HeaderTitleService,
    @Optional() private dialogRef: MatDialogRef<SurveyDetailsComponent>,
  ) {}

  faLock = faLock;
  faGlobe = faGlobe;
  faPaperclip = faPaperclip;
  loading = false;
  createMode = true;
  survey = {
    id: null,
    title: '',
    description: '',
    edit_after_submit: false,
    multiple_user_responses_is_enabled: false,
    visibility_type: 'PRIVATE',
    attachments_enabled: true,
    questions: [],
    members: [],
    attachments: [],
    email_template_id: null,
    is_draft: false,
    organization_project_id: null,
    answered: false,
    ckm_map_id: null,
    status: null,
    survey_history_id: null,
    source_id: null,
    customs: {
      draft_answer_is_enabled: false,
      anonymous_survey: false,
      anagraphic_is_mandatory: false,
      anagraphic_position: 'START',
      skip_introduction: false,
      speech_to_text_enabled: false,
      medical_speech_to_text_enabled: false,
    },
    typeSubmitted: null,
    is_edited_on_revision: false,
    statement_number: null,
    updated_at: null as string | null,
  };
  sections = null;
  questions_filters = {
    selected_section: null,
    only_not_answered: false,
    accordions_opened: true,
  };
  previewMode = false;
  email_templates = [];
  private questionIdCounter = 1;
  private memberIdCounter = 1;
  preSurvey = null;
  _intervalId = null;
  private destroy$ = new Subject<void>();
  private autoSaveSubject = new Subject<void>();
  private forceSaveSubject = new Subject<void>();
  deletedQuestionIds: number[] = [];
  autoSaveLoading = false;
  faCheckCircle = faCheckCircle;
  faInfoCircle = faInfoCircle;
  faClipboardList = faClipboardList;
  faSlidersH = faSlidersH;
  faUsers = faUsers;

  // Attachment analysis trigger
  triggeringAttachmentAnalysis = false;
  attachmentAnalysisInProgress = false;
  attachmentsAlreadyProcessed = false;
  private attachmentPolling$: Subscription | null = null;
  private evidencesDialogRef: MatDialogRef<ModalShowEvidencesComponent> | null = null;

  // Survey-level attachments analysis
  loadingSurveyAttachments = false;
  surveyProcessedAttachments: any[] = [];
  expandedSurveyAttachmentId: number | null = null;
  surveyAttachmentColumns: string[] = [
    'name',
    'type',
    'level',
    'status',
    'date',
    'analysis',
  ];
  hasSurveyAttachmentsFlag = false;

  // Custom fields for team members
  customFieldTypes = [
    { value: 'text', label: this.translateService.instant('generic.text') },
    { value: 'number', label: this.translateService.instant('generic.number') },
    { value: 'date', label: this.translateService.instant('generic.date') },
    { value: 'boolean', label: this.translateService.instant('generic.s-n') },
  ];
  newCustomField = { name: '', type: 'text', visible: false, required: false };
  editingCustomField: any = null;
  editCustomFieldModel = {
    name: '',
    type: 'text',
    visible: false,
    required: false,
  };
  customFieldLoading = false;

  ngOnInit(): void {
    this.init();
    this.setupAutosave();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['surveyObj'] && this.surveyObj) {
      this.survey = this.surveyObj;
      this.normalizeSurvey();
    }
  }

  private setupAutosave(): void {
    merge(
        this.autoSaveSubject.pipe(debounceTime(2000)),
        this.forceSaveSubject
    ).pipe(
      takeUntil(this.destroy$),
      filter(
        () =>
          this.createMode &&
          this.hasDifferences() &&
          !this.loading &&
          !this.autoSaveLoading &&
          !!this.survey?.title?.trim(),
      ),
      switchMap(() => {
        this.autoSaveLoading = true;
        const payload = this.buildSyncPayload();
        // Mettiamo la survey id reale oppure quella temporanea se esiste
        if (this.surveyId) {
            return this.surveyService.syncDraft(this.surveyId, payload).pipe(
               catchError(err => {
                 console.error('Errore nel salvataggio auto-draft:', err);
                 this.autoSaveLoading = false;
                 return of(null);
               })
            );
        } else {
            // Se la survey non è mai stata salvata prima e non ha un ID, creiamola prima usando onSubmit
            this.onSubmit(
              null,
              this.surveyService.surveySubmittedTypologies.IS_NEW_DRAFT,
              true,
            );
            return of(null);
        }
      })
    ).subscribe((response: any) => {
       if (response && response.mapped_ids) {
          // Aggiorna gli ID nulli con i nuovi ID del DB
          this.updateLocalIds(response.mapped_ids);
          this.deletedQuestionIds = []; // Svuota il cestino dopo un sync riuscito
          this.syncPreSurvey(); // aggiorniamo lo stato precedente
          this.survey.updated_at = new Date().toISOString(); // Aggiorna il last saved
       }
       this.autoSaveLoading = false;
    });

    // Inneschiamo comunque un controllo periodico ogni 10 secondi per coprire eventuali modifiche passate inosservate
    timer(10000, 10000).pipe(
        takeUntil(this.destroy$)
    ).subscribe(() => {
        if (this.createMode && this.hasDifferences() && !this.loading && !this.autoSaveLoading && !!this.survey?.title?.trim()) {
            this.autoSaveSubject.next();
        }
    });
  }

  private buildSyncPayload() {
    return {
      client_last_updated_at: this.survey.updated_at || new Date().toISOString(),
      survey: {
         title: this.survey.title,
         description: this.survey.description,
         customs: this.survey.customs,
         multiple_user_responses_is_enabled: this.survey.multiple_user_responses_is_enabled,
         edit_after_submit: this.survey.edit_after_submit,
         visibility_type: this.survey.visibility_type,
         attachments_enabled: this.survey.attachments_enabled,
         email_template_id: this.survey.email_template_id,
         ckm_map_id: this.survey.ckm_map_id,
         members: this.survey.members || []
      },
      questions: {
        upsert: this.survey.questions?.filter((q: any) => {
             if (!this.preSurvey || !this.preSurvey.questions) return true;
             const oldQ = this.preSurvey.questions.find((old: any) => old.id === q.id);
             if (!oldQ) return true;
             return this.hasDiff(oldQ, q);
        }) || [],
        delete: this.deletedQuestionIds
      }
    };
  }

  private updateLocalIds(mappedIds: any) {
     if (mappedIds.questions) {
         this.survey.questions.forEach((q, index) => {
             const tempIdKey = q.id || "index_" + index;
             if (mappedIds.questions[tempIdKey]) {
                 q.id = mappedIds.questions[tempIdKey];
             }

             if (mappedIds.attachments && q.attachments) {
                 q.attachments.forEach((a: any) => {
                     if (mappedIds.attachments[a.id]) {
                         const newAtt = mappedIds.attachments[a.id];
                         if (newAtt.path) a.path = newAtt.path;
                         if (newAtt.link) a.link = newAtt.link;
                         a.id = newAtt.id;
                         delete a.original_attachment_id;
                         delete a.isDuplicate;
                     }
                 });
             }
         });
     }

     if (mappedIds.choices) {
         this.survey.questions.forEach((q) => {
             if (q.choices) {
                 q.choices.forEach((c, index) => {
                     const tempIdKey = c.id || "index_" + index;
                     if (mappedIds.choices[tempIdKey]) {
                         c.id = mappedIds.choices[tempIdKey];
                     }
                 });
             }
         });
     }

     if (mappedIds.grid_rows) {
         this.survey.questions.forEach((q) => {
             if (q.grid_rows) {
                 q.grid_rows.forEach((r, index) => {
                     const tempIdKey = r.id || "index_" + index;
                     if (mappedIds.grid_rows[tempIdKey]) {
                         r.id = mappedIds.grid_rows[tempIdKey];
                     }
                 });
             }
         });
     }

     if (mappedIds.grid_columns) {
         this.survey.questions.forEach((q) => {
             if (q.grid_columns) {
                 q.grid_columns.forEach((c, index) => {
                     const tempIdKey = c.id || "index_" + index;
                     if (mappedIds.grid_columns[tempIdKey]) {
                         c.id = mappedIds.grid_columns[tempIdKey];
                     }
                 });
             }
         });
     }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this._intervalId) {
      clearInterval(this._intervalId);
    }
    this.stopAttachmentPolling();
  }

  init() {
    this.getSections();
    if (
      this.userService?.currentOrganization?.id &&
      this.userService.userDetails
    ) {
      this.organizationService.allOrganizationTemplatesEmail(
        this.userService?.currentOrganization?.id,
        (data) => {
          this.email_templates = data;
        },
      );
    }

    this.survey.ckm_map_id = this.ckmMapID;

    if (this.isResponse) {
      this.createMode = false;
    }

    if (this.surveyObj) {
      this.survey = this.surveyObj;
      this.normalizeSurvey();
      return;
    }

    if (!this.projectId) {
      this.canceled.emit();
    }

    if (this.surveyId) {
      this.createMode = false;
      this.getSurvey();
      this.checkIfHasSurveyAttachments();
      this.checkAttachmentAnalysisStatus();
    } else {
      if (this.ckmMapID) {
        this.loading = true;
        this.ckmService.getCkmMapSurveyQuestions(
          this.ckmMapID,
          (response) => {
            response?.forEach((question) => {
              let min = null;
              let max = null;
              if (question?.ckm_entity_type === 'event') {
                min = 0;
                max = 100;
              } else if (question?.ckm_entity_type === 'action') {
                min = 1;
                max = 5;
              } else {
                min = 0;
                max = 5;
              }

              const obj = {
                id: this.questionIdCounter++,
                question: question?.question,
                required: true,
                typology: this.surveyService.questionTypologies[1].id,
                choices: [],
                description: '',
                attachments: [],
                response: {
                  customs: {},
                  attachments: [],
                },
                customs: {
                  feedback_is_mandatory: false,
                  use_description: false,
                  use_slider: false,
                  tricolor: false,
                  min,
                  max,
                  step: 0.1,
                  measure_unit:
                    question?.ckm_entity_type == 'event'
                      ? 'PERCENTAGE'
                      : 'POINT',
                  section: this.formatString(question?.ckm_entity_type),
                  use_custom_min_max_labels: false,
                  ckm_entity_type: question?.ckm_entity_type,
                  ckm_entity_id: question?.ckm_entity_id,
                },
              };
              this.addQuestion(obj);
            });
            this.loading = false;
          },
          (error) => {
            this.loading = false;
          },
        );
      } else {
        this.addQuestion();
      }
      this.survey.members.push({
        id: this.memberIdCounter++,
        name: '',
        last_name: '',
        email: '',
        role: '',
        is_respondent: true,
      });
      Promise.resolve().then(() => this.surveyChanged.emit(this.survey));
      this.preSurvey = JSON.parse(JSON.stringify(this.survey));
    }
  }

  openSurveyImportModal() {
    const dialogRef = this.dialog.open(ModalShowSurveysComponent, {
      panelClass: 'medium-dialog',
      data: {
        project_id: this.projectId,
      },
    });
    dialogRef.afterClosed().subscribe((event) => {
      if (event) {
        const survey = { ...event };
        this.surveyId = survey.id;
        this.getSurvey(true);
      }
    });
  }

  onSpeechToTextEnabledChange(enabled: boolean): void {
    if (!enabled) {
      this.survey.customs.medical_speech_to_text_enabled = false;
    }
    this.handleSurveyChanged();
  }

  handleSurveyChanged() {
    if (this.survey.visibility_type === 'PRIVATE') {
      this.teamMemberCustomFields.forEach((field) => {
        field.visible = false;
        field.required = false;
      });
      // Also update new field defaults
      this.newCustomField.visible = false;
      this.newCustomField.required = false;
    } else {
      this.newCustomField.visible = true;
    }
    Promise.resolve().then(() => this.surveyChanged.emit(this.survey));
  }

  getSurvey(isImport = false) {
    this.loading = true;
    this.surveyService.surveyDetails(
      this.surveyId,
      (result) => {
        this.survey = result;
        this.normalizeSurvey();
        this.preSurvey = JSON.parse(JSON.stringify(this.survey));
        const maxQuestionId = result.questions?.reduce((maxId, question) => {
          return Math.max(maxId, question.id);
        }, 1);
        this.questionIdCounter = maxQuestionId + 1;
        Promise.resolve().then(() => this.surveyChanged.emit(this.survey));
        if (result?.is_draft) {
          this.createMode = true;
        }
        this.loading = false;
        if (isImport) {
          this.survey.id = null;
          this.survey.survey_history_id = null;
          this.survey.source_id = null;
          this.survey.questions?.forEach((question) => {
            question.question_history_id = null;
            question.source_id = null;
          });
          this.toast.success(
            this.translateService.instant('generic.success'),
            this.translateService.instant(
              'modals.modal-survey-details.survey-cloned-successfully',
            ),
          );
        }
        this.preSurvey = JSON.parse(JSON.stringify(this.survey));
        this.headerTitleService.setTitle(result.title);
      },
      () => {
        this.loading = false;
      },
    );
  }

  get getLastSavedLabel(): string {
    const updatedAt = this.survey?.updated_at;
    if (!updatedAt) {
      return '';
    }
    const updated = new Date(updatedAt).getTime();
    const now = Date.now();
    const diffSeconds = Math.floor((now - updated) / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
      return `Salvato automaticamente ${diffSeconds} sec fa`;
    }
    if (diffMinutes < 60) {
      return `Salvato automaticamente ${diffMinutes} min fa`;
    }
    if (diffHours < 24) {
      const ore = diffHours === 1 ? 'ora' : 'ore';
      return `Salvato automaticamente ${diffHours} ${ore} fa`;
    }
    const giorni = diffDays === 1 ? 'giorno' : 'giorni';
    return `Salvato automaticamente ${diffDays} ${giorni} fa`;
  }

  get surveyIsPrivate() {
    return this.survey.visibility_type === 'PRIVATE';
  }

  get membersAreValid() {
    if (this.surveyIsPrivate) {
      return !(
        this.survey.members.length === 0 ||
        this.survey.members.some((member) => !member.name || !member.email)
      );
    }
    return true;
  }

  showEvidences() {
    this.evidencesDialogRef = this.dialog.open(ModalShowEvidencesComponent, {
      panelClass: 'medium-dialog',
      data: {
        attachments: this.survey?.attachments || [],
        editable: this.createMode || this.userService.currentUserIsOwnerOrEditor(),
        surveyId: this.survey?.id,
        getSurveyId: () => this.survey?.id,
      },
    });
    this.evidencesDialogRef.afterClosed().subscribe(() => {
      this.evidencesDialogRef = null;
    });
  }

  surveyIsValid() {
    const status = this.surveyService.surveyIsValid(this.survey) as any;
    if (!status?.valid) {
      this.toast.error(
        this.translateService.instant('generic.warning'),
        this.translateService.instant(status?.message),
      );
      return false;
    }

    return true;
  }

  onSubmit(
    form: NgForm = null,
    typeSubmitted: string = this.surveyService.surveySubmittedTypologies.IS_NEW,
    autoSave = false,
  ) {
    const projectId = this.projectId || this.survey.organization_project_id;
    this.autoSaveLoading = autoSave;
    this.loading = true;
    this.survey.typeSubmitted = typeSubmitted;

    // Clear members array for public surveys
    if (this.survey.visibility_type === 'PUBLIC') {
      this.survey.members = [];
    }

    this.checkEditedStatusForAll();

    this.survey.is_edited_on_revision = this.survey?.questions?.some(
      (question) => question.is_edited_on_revision || question?.is_new,
    );

    if (
      typeSubmitted ===
      this.surveyService.surveySubmittedTypologies.IS_NEW_DRAFT
    ) {
      this.handleDraftSubmit(projectId, autoSave);
    } else {
      this.handleSurveySubmit(projectId, form);
    }
  }

  private handleDraftSubmit(projectId: any, autoSave: boolean): void {
    if (
      typeof this.survey?.title !== 'string' ||
      this.survey.title.trim().length === 0
    ) {
      this.loading = false;
      this.toast.error(
        this.translateService.instant('generic.warning'),
        this.translateService.instant(
          'modals.modal-survey-details.draft-title-is-mandatory',
        ),
      );
      return;
    }

    this.survey.is_draft = true;
    this.surveyService.createSurvey(
      projectId,
      this.survey,
      (result) => {
        this.surveyAdded.emit(result);
        this.survey = result;
        this.surveyId = result.id;
        this.normalizeSurvey();

        if (this.survey.title === 'Survey (draft)') {
          this.survey.title = `Survey ${result.id} (draft)`;
          this.surveyService.createSurvey(projectId, this.survey, (res) => {
            this.survey = res;
            this.normalizeSurvey();
            this.syncPreSurvey();
          });
        }

        if (autoSave) {
          this.router.navigate(['/surveys', result.id]);
          this.questionComponents?.forEach((component) => component.reset());
          this.syncPreSurvey();
        } else {
          this.toast.success(
            this.translateService.instant('generic.success'),
            this.translateService.instant('generic.draft-saved-successfully'),
          );
          this.router.navigate(['/']).then(() => {
            this.router.navigate(['/surveys', result.id]);
          });
        }
        this.autoSaveLoading = false;
        this.loading = false;
      },
      () => {
        this.loading = false;
        this.autoSaveLoading = false;
      },
    );
  }

  private handleSurveySubmit(projectId: any, form: NgForm): void {
    this.survey.is_draft = false;

    if (!this.surveyIsValid() || !this.membersAreValid) {
      this.loading = false;
      this.survey.is_draft = true;
      if (!this.membersAreValid) {
        this.toast.error(
          this.translateService.instant('generic.warning'),
          this.translateService.instant(
            'components.decision-table.new-decision-table.compile-proposal.fourth-error',
          ),
        );
      }
      return;
    }

    if (form && !form.form.valid) {
      this.toast.error(
        this.translateService.instant('generic.warning'),
        this.translateService.instant('pages.surveys.survey-details.not-valid'),
      );
      this.loading = false;
      this.survey.is_draft = true;
      return;
    }

    const createSurveyCallback = (result) => {
      this.toast.success(
        this.translateService.instant('generic.success'),
        this.translateService.instant(
          'modals.modal-survey-details.survey-created-successfully',
        ),
      );
      this.toast.success(
        this.translateService.instant('generic.success'),
        this.translateService.instant(
          'modals.modal-survey-details.survey-team-members-invited',
        ),
      );
      this.surveyAdded.emit(result);
      this.survey = result;
      this.normalizeSurvey();
      this.syncPreSurvey();
      this.loading = false;
      this.router.navigate(['/']).then(() => {
        this.router.navigate(['/surveys', result.id]);
      });
    };

    const handleSurveyCreationFailure = () => {
      this.survey.is_draft = true;
      this.loading = false;
    };

    if (
      !this.survey.email_template_id &&
      this.surveyService.surveySubmittedTypologies.IS_NEW ===
        this.survey.typeSubmitted
    ) {
      this.alert.showQuestion(
        this.translateService.instant('generic.warning'),
        this.translateService.instant('pages.template.no-selected-template'),
        (ok) => {
          this.surveyService.createSurvey(
            projectId,
            this.survey,
            createSurveyCallback,
            handleSurveyCreationFailure,
          );
        },
        handleSurveyCreationFailure,
      );
    } else {
      this.surveyService.createSurvey(
        projectId,
        this.survey,
        createSurveyCallback,
        handleSurveyCreationFailure,
      );
    }
  }

  private processDraftAttachments(): void {
    this.survey?.attachments?.forEach((attachment) => {
      if (!attachment?.survey_question_id) {
        attachment.survey_question_id = 'UPLOADED_IN_DRAFT';
      }
    });
    this.survey?.questions?.forEach((q) => {
      q.attachments?.forEach((attachment) => {
        if (!attachment?.survey_question_id) {
          attachment.survey_question_id = 'UPLOADED_IN_DRAFT';
        }
      });
    });
  }

  private syncPreSurvey(): void {
    this.preSurvey = JSON.parse(JSON.stringify(this.survey));
  }

  private normalizeSurvey(): void {
    if (!this.survey) return;

    if (!this.survey.customs) {
      this.survey.customs = {
        draft_answer_is_enabled: false,
        anonymous_survey: false,
        anagraphic_is_mandatory: false,
        anagraphic_position: 'before',
        skip_introduction: false,
        speech_to_text_enabled: false,
        medical_speech_to_text_enabled: false,
      };
    }

    this.survey.customs.speech_to_text_enabled = !!this.survey.customs.speech_to_text_enabled;
    this.survey.customs.medical_speech_to_text_enabled = !!this.survey.customs
      .medical_speech_to_text_enabled;
    if (!this.survey.customs.speech_to_text_enabled) {
      this.survey.customs.medical_speech_to_text_enabled = false;
    }

    // Mappatura allegati a livello Survey
    const surveyFiles =
      (this.survey['attachment_files'] as any[])?.map((f) => ({
        ...f,
        type: f.type || 'FILE',
        isNew: false,
      })) || [];
    const surveyPubmeds =
      (this.survey['attachment_pubmeds'] as any[])?.map((p) => ({
        ...p,
        type: 'PUBMED',
        isNew: false,
      })) || [];

    if (surveyFiles.length > 0 || surveyPubmeds.length > 0) {
      this.survey.attachments = [...surveyFiles, ...surveyPubmeds];
    } else if (!this.survey.attachments) {
      this.survey.attachments = [];
    }

    if (this.evidencesDialogRef) {
      this.evidencesDialogRef.componentInstance.updateAttachments(this.survey.attachments);
    }

    // Mappatura allegati a livello Question
    if (this.survey.questions) {
      this.survey.questions.forEach((q: any) => {
        const qFiles =
          (q['attachment_files'] as any[])?.map((f) => ({
            ...f,
            type: f.type || 'FILE',
            isNew: false,
          })) || [];
        const qPubmeds =
          (q['attachment_pubmeds'] as any[])?.map((p) => ({
            ...p,
            type: 'PUBMED',
            isNew: false,
          })) || [];

        if (qFiles.length > 0 || qPubmeds.length > 0) {
          q.attachments = [...qFiles, ...qPubmeds];
        } else if (!q.attachments) {
          q.attachments = [];
        }

        // Se è una risposta, pulisco isNew anche nel sotto-array response se presente
        if (q.response?.attachments) {
          q.response.attachments.forEach((a: any) => (a.isNew = false));
        }
      });
    }
  }

  deleteSurvey() {
    this.loading = true;
    this.surveyService.deleteSurvey(
      this.survey.id,
      (result) => {
        this.loading = false;
        this.canceled.emit(this.survey);
        this.toast.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant(
            'modals.modal-survey-details.survey-deleted-successfully',
          ),
        );
      },
      () => {
        this.loading = false;
      },
    );
  }

  drop(event: CdkDragDrop<object[]>) {
    moveItemInArray(
      this.survey?.questions,
      event.previousIndex,
      event.currentIndex,
    );
  }

  addQuestion(question = null) {
    if (question) {
      this.survey.questions?.push({
        ...question,
        id: this.questionIdCounter++,
        is_new: true,
      });

      console.log('Question added with ID:', this.questionIdCounter - 1);
      console.log(this.createMode)
      if (this.createMode) {
          this.forceSaveSubject.next();
      }
    } else {
      this.survey.questions?.push({
        id: this.questionIdCounter++,
        question: 'Question ' + (this.survey?.questions?.length + 1),
        required: true,
        typology: this.surveyService.questionTypologies[0].id,
        choices: [],
        description: '',
        attachments: [],
        response: {
          customs: {},
          attachments: [],
        },
        customs: {
          feedback_is_mandatory: false,
          use_description: false,
          use_slider: false,
          tricolor: false,
          min: null,
          max: null,
          step: 1,
          use_custom_min_max_labels: false,
          use_cascade_select: false,
        },
        is_new: true,
      });
    }
  }

  getSections() {
    const sections = [
      {
        id: 'DETAILS',
        title: 'modals.modal-survey-details',
      },
    ];

    if (this.surveyId) {
      sections.push({
        id: 'MEMBERS',
        title: 'generic.team',
      });
    }

    sections.push({
      id: 'QUESTIONS',
      title: 'generic.questions',
    });

    this.sections = sections;
  }

  removeQuestion(id) {
    if (typeof id === 'number' && !id.toString().startsWith('temp_')) {
        this.deletedQuestionIds.push(id);
    }
    this.survey.questions = this.survey.questions.filter(
      (question) => question.id !== id,
    );
    this.surveyChanged.emit(this.survey);
    this.autoSaveSubject.next();
  }

  copyLink() {
    const path =
      environment.logoutReturnUrl +
      '#/' +
      'surveys/' +
      this.surveyId +
      '/response';
    navigator.clipboard.writeText(path);
    this.toast.success(
      this.translateService.instant('generic.success'),
      this.translateService.instant('generic.link-copied'),
    );
  }

  openQrDialog() {
    const path =
      environment.logoutReturnUrl +
      '#/' +
      'surveys/' +
      this.surveyId +
      '/response';
    this.dialog.open(ModalShareQrComponent, {
      panelClass: 'medium-dialog',
      data: {
        link: path,
      },
    });
  }

  reviewSurvey() {
    this.alert.showQuestion(
      this.translateService.instant('generic.warning'),
      this.translateService.instant(
        'modals.modal-survey-details.new-revision-warning',
      ),
      (ok) => {
        this.onSubmit(
          null,
          this.surveyService.surveySubmittedTypologies.IS_NEW_REVISION,
        );
      },
      () => {},
    );
  }

  startNewSurveySession() {
    this.alert.showQuestion(
      this.translateService.instant('generic.warning'),
      this.translateService.instant(
        'modals.modal-survey-details.new-revision-session-warning',
      ),
      (ok) => {
        this.onSubmit(
          null,
          this.surveyService.surveySubmittedTypologies.IS_NEW_SESSION,
        );
      },
      () => {},
    );
  }

  downloadResponsesCSV() {
    this.loading = true;
    this.surveyService.downloadResponsesCSV(
      this.survey.id,
      (response) => {
        this.loading = false;
      },
      () => {
        this.loading = false;
      },
    );
  }

  get question_sections() {
    return [
      ...new Set(
        this.survey?.questions?.map((question) => question?.customs?.section),
      ),
    ].filter((section) => section);
  }

  get filteredQuestions() {
    let questions = this.survey.questions;

    if (this.questions_filters.selected_section) {
      questions = questions.filter(
        (question) =>
          question?.customs?.section ===
          this.questions_filters.selected_section,
      );
    }

    if (this.questions_filters.only_not_answered) {
      questions = questions.filter(
        (question) =>
          !this.surveyService.surveyQuestionAnswerIsValid(question, false)
            ?.valid,
      );
    }

    return questions;
  }

  enablePreviewMode() {
    this.survey?.questions.forEach((question) => {
      if (question?.response === undefined) {
        question.response = {
          customs: {},
        };
      }
    });
    this.isResponse = true;
    this.createMode = false;
    this.previewMode = true;
    this.sections = this.sections.filter((section) => section.id !== 'DETAILS');
  }

  disablePreviewMode() {
    this.isResponse = false;
    this.createMode = true;
    this.previewMode = false;
    this.sections.unshift({
      id: 'DETAILS',
      title: 'modals.modal-survey-details',
    });
  }

  get currentUserInTeam() {
    return this.survey?.members?.find(
      (member: any) => member?.user_id == this.userService.userDetails?.user_id,
    );
  }

  formatString(str): void {
    return str
      .replace(/_/g, ' ') // sostituisce tutti gli underscore con spazi
      .split(' ') // divide la stringa in parole
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // capitalizza ogni parola
      .join(' '); // ricompone la stringa
  }

  checkEditedStatusForAll(): void {
    this.questionComponents?.forEach((component, index) => {
      component.isEdited();
    });
  }

  private hasDiff(a: any, b: any, ignoredKeys: string[] = []): boolean {
    if (a === b) {
      return false;
    }
    if (a == null || b == null) {
      return a !== b;
    }

    if (typeof a !== typeof b) {
      return true;
    }

    if (typeof a === 'object') {
      const keys = Array.from(
        new Set([...Object.keys(a || {}), ...Object.keys(b || {})]),
      ).filter((k) => !ignoredKeys.includes(k));

      for (const key of keys) {
        if (this.hasDiff(a[key], b[key], ignoredKeys)) {
          return true;
        }
      }

      return false;
    }

    return a !== b;
  }

  hasDifferences(): boolean {
    return this.hasDiff(this.preSurvey, this.survey);
  }

  /**
   * Load all survey-level attachments with LLM analysis
   */
  loadSurveyAttachments(): void {
    if (!this.surveyId) {
      console.error('Missing surveyId');
      return;
    }

    this.loadingSurveyAttachments = true;

    this.surveyService.getAttachmentsLLMAnalysis(
      this.surveyId.toString(),
      '', // No question ID for survey-level
      undefined,
      (result) => {
        // Handle different response formats
        if (Array.isArray(result)) {
          this.surveyProcessedAttachments = result;
        } else if (result && typeof result === 'object') {
          this.surveyProcessedAttachments =
            result.data || result.attachments || result.results || [];
        } else {
          this.surveyProcessedAttachments = [];
        }

        this.loadingSurveyAttachments = false;
      },
      (error) => {
        console.error('Error loading survey attachments:', error);
        this.loadingSurveyAttachments = false;
        this.surveyProcessedAttachments = [];
        this.toast.error(
          this.translateService.instant('generic.error'),
          'Failed to load survey attachments',
        );
      },
    );
  }

  /** Caricamento dati al primo expand del pannello (coerente con expansion-panels-cards). */
  onSurveyAttachmentsPanelOpened(): void {
    if (this.surveyProcessedAttachments.length === 0) {
      this.loadSurveyAttachments();
    }
  }

  onSurveyAttachmentsPanelClosed(): void {
    this.expandedSurveyAttachmentId = null;
  }

  /**
   * Toggle the expansion of a survey attachment's analysis
   */
  toggleSurveyAttachmentExpansion(attachmentId: number): void {
    if (this.expandedSurveyAttachmentId === attachmentId) {
      this.expandedSurveyAttachmentId = null;
    } else {
      this.expandedSurveyAttachmentId = attachmentId;
    }
  }

  /**
   * Check if survey has attachments by calling the endpoint
   */
  checkIfHasSurveyAttachments(): void {
    if (!this.surveyId) {
      this.hasSurveyAttachmentsFlag = false;
      return;
    }

    this.surveyService.getAttachmentsLLMAnalysis(
      this.surveyId.toString(),
      '', // No question ID for survey-level
      undefined,
      (result) => {
        let attachments: any[] = [];
        const stateSummary = result?.state_summary ?? {};

        if (Array.isArray(result)) {
          attachments = result;
        } else if (result && typeof result === 'object') {
          attachments =
            result.attachments || result.data || result.results || [];
        }

        this.hasSurveyAttachmentsFlag = attachments.length > 0;

        const total: number = result?.total_count ?? attachments.length;
        const waiting: number = stateSummary.waiting ?? 0;
        const processing: number = stateSummary.processing ?? 0;
        const completed: number = stateSummary.completed ?? 0;
        const failed: number = stateSummary.failed ?? 0;

        // All records are in a final state (none waiting/processing) and at least one exists
        this.attachmentsAlreadyProcessed =
          total > 0 && waiting === 0 && processing === 0 && (completed + failed) === total;
      },
      (error) => {
        console.error('Error checking survey attachments:', error);
        this.hasSurveyAttachmentsFlag = false;
        this.attachmentsAlreadyProcessed = false;
      },
    );
  }

  /**
   * Check if survey has attachments to determine if "View Attachments" button should be displayed
   */
  get hasSurveyAttachments(): boolean {
    return this.hasSurveyAttachmentsFlag;
  }

  get hasSurveyAttachmentsToAnalyze(): boolean {
    const s = this.survey as any;
    return (s?.attachment_files?.length > 0) || (s?.attachment_pubmeds?.length > 0) || (s?.attachments?.length > 0);
  }

  // ─── Custom Fields for Team Members ───────────────────────────────────────

  get teamMemberCustomFields(): any[] {
    return (this.survey as any)?.team_member_custom_fields ?? [];
  }

  addCustomField(): void {
    const fieldName = this.newCustomField.name.trim();

    // Check for duplicates (case-insensitive)
    if (
      this.teamMemberCustomFields.some(
        (f) => f.name.toLowerCase() === fieldName.toLowerCase(),
      )
    ) {
      this.toast.error(
        this.translateService.instant('generic.warning'),
        'Esiste già un campo con questo nome',
      );
      return;
    }

    const fieldData = {
      name: fieldName,
      type: this.newCustomField.type,
      visible:
        this.survey.visibility_type === 'PUBLIC'
          ? this.newCustomField.visible
          : false,
      required:
        this.survey.visibility_type === 'PUBLIC'
          ? this.newCustomField.required
          : false,
    };

    if (!this.surveyId) {
      // If survey not yet saved, add to local array and trigger first draft save
      if (!this.survey.title?.trim()) {
        this.survey.title = 'Survey (draft)';
      }

      const tempField = { ...fieldData, id: 'temp_' + Date.now() };
      (this.survey as any).team_member_custom_fields = [
        ...this.teamMemberCustomFields,
        tempField,
      ];
      this.newCustomField = {
        name: '',
        type: 'text',
        visible: true,
        required: false,
      };

      // This will create the survey and the backend will save the custom fields too
      this._saveDraft();
    } else {
      // Standard flow: call API directly
      this.customFieldLoading = true;
      this.surveyService.addTeamMemberCustomField(
        this.surveyId,
        fieldData,
        (result) => {
          (this.survey as any).team_member_custom_fields = [
            ...this.teamMemberCustomFields.filter(
              (f) => !f.id?.toString().startsWith('temp_'),
            ),
            result.data ?? result,
          ];
          this.newCustomField = {
            name: '',
            type: 'text',
            visible: true,
            required: false,
          };
          this.customFieldLoading = false;
          if (this.survey.is_draft) {
            this._saveDraft();
          }
        },
        () => {
          this.customFieldLoading = false;
        },
      );
    }
  }

  deleteCustomField(field: any): void {
    this.alert.showQuestion(
      this.translateService.instant('generic.warning'),
      'Vuoi eliminare il campo "' + field.name + '"?',
      () => {
        this.customFieldLoading = true;
        this.surveyService.deleteTeamMemberCustomField(
          this.surveyId,
          field.id,
          () => {
            (this.survey as any).team_member_custom_fields =
              this.teamMemberCustomFields.filter((f) => f.id !== field.id);
            this.customFieldLoading = false;
            if (this.survey.is_draft) {
              this._saveDraft();
            }
          },
          () => {
            this.customFieldLoading = false;
          },
        );
      },
    );
  }

  startEditCustomField(field: any): void {
    this.editingCustomField = field;
    this.editCustomFieldModel = {
      name: field.name,
      type: field.type,
      visible: field.visible !== undefined ? field.visible : true,
      required: field.required !== undefined ? field.required : false,
    };
  }

  cancelEditCustomField(): void {
    this.editingCustomField = null;
  }

  saveEditCustomField(): void {
    if (!this.editCustomFieldModel.name?.trim()) {
      this.toast.error(
        this.translateService.instant('generic.warning'),
        this.translateService.instant(
          'components.surveys.survey-team.survey-team.custom-field-title-is-mandatory',
        ),
      );
      return;
    }
    this.customFieldLoading = true;
    this.surveyService.updateTeamMemberCustomField(
      this.surveyId,
      this.editingCustomField.id,
      {
        name: this.editCustomFieldModel.name.trim(),
        type: this.editCustomFieldModel.type,
        visible:
          this.survey.visibility_type === 'PUBLIC'
            ? this.editCustomFieldModel.visible
            : false,
        required:
          this.survey.visibility_type === 'PUBLIC'
            ? this.editCustomFieldModel.required
            : false,
      },
      (result) => {
        const updatedField = result.data ?? result;
        const index = (this.survey as any).team_member_custom_fields.findIndex(
          (f) => f.id === this.editingCustomField.id,
        );
        if (index !== -1) {
          (this.survey as any).team_member_custom_fields[index] = updatedField;
        }
        this.editingCustomField = null;
        this.customFieldLoading = false;
        if (this.survey.is_draft) {
          this._saveDraft();
        }
      },
      () => {
        this.customFieldLoading = false;
      },
    );
  }

  checkAttachmentAnalysisStatus(): void {
    if (!this.surveyId) return;
    this.surveyService.checkProcessingAttachments(
      this.surveyId.toString(),
      (result) => {
        if (result?.has_processing_attachments) {
          this.attachmentAnalysisInProgress = true;
          this.startAttachmentPolling();
        } else {
          this.attachmentAnalysisInProgress = false;
        }
      },
    );
  }

  private startAttachmentPolling(): void {
    if (this.attachmentPolling$) return; // already polling
    this.attachmentPolling$ = interval(10000)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => {
          return new Promise<boolean>((resolve) => {
            this.surveyService.checkProcessingAttachments(
              this.surveyId.toString(),
              (result) => resolve(result?.has_processing_attachments ?? false),
              () => resolve(false),
            );
          });
        }),
      )
      .subscribe((stillInProgress: boolean) => {
        if (!stillInProgress) {
          this.attachmentAnalysisInProgress = false;
          this.stopAttachmentPolling();
          // Refresh the completed-state flag now that the job finished
          this.checkIfHasSurveyAttachments();
        }
      });
  }

  private stopAttachmentPolling(): void {
    if (this.attachmentPolling$) {
      this.attachmentPolling$.unsubscribe();
      this.attachmentPolling$ = null;
    }
  }

  triggerAttachmentAnalysis(): void {
    if (
      !this.surveyId ||
      this.triggeringAttachmentAnalysis ||
      this.attachmentAnalysisInProgress
    ) {
      return;
    }
    this.triggeringAttachmentAnalysis = true;
    this.surveyService.triggerAttachmentAnalysis(
      this.surveyId.toString(),
      (result) => {
        this.triggeringAttachmentAnalysis = false;
        this.attachmentAnalysisInProgress = true;
        this.startAttachmentPolling();
        this.toast.success(
          this.translateService.instant('generic.success'),
          result?.message ?? 'Attachment analysis job dispatched',
        );
      },
      (error) => {
        this.triggeringAttachmentAnalysis = false;
        if (error?.status === 409) {
          this.attachmentAnalysisInProgress = true;
          this.startAttachmentPolling();
          this.toast.info(
            this.translateService.instant('generic.info') ?? 'Info',
            error?.error?.message ??
              'Attachment analysis already exists or is in progress',
          );
        }
      },
    );
  }

  private _saveDraft(): void {
    // If ID exists, just save as draft update
    if (this.surveyId) {
      this.autoSaveSubject.next();
    } else {
      // If no ID, only save if title is present (to avoid errors)
      if (
        typeof this.survey?.title === 'string' &&
        this.survey.title.trim().length > 0
      ) {
        this.onSubmit(
          null,
          this.surveyService.surveySubmittedTypologies.IS_NEW_DRAFT,
          true,
        );
      }
    }
  }
}
